<template>
  <substrate-mount show-tail>
    <div :class="$style.title">
      {{ $t('report.parameters') }}
    </div>

    <div :class="$style.panel">
      <selectabling-panel
        v-if="isTransactionReport"
        v-model="panel.transactionTypes"
        :label="$t('transactions.type')"
        @click="handleTransactionTypes"
      />
      <div :class="$style.tooltip">
        <report-tooltip :message="tooltip.transactionTypes" />
      </div>
    </div>

    <selectabling-panel
      v-if="isTransactionReport"
      v-model="panel.transactionStatuses"
      :label="$t('transactions.status')"
      @click="handleTransactionStatuses"
    />

    <div :class="$style.panel">
      <selectabling-panel
        v-if="isPartnerReport"
        v-model="panel.partners"
        :label="$t('common.partners')"
        @click="handlePartners"
      />
      <div :class="$style.tooltip">
        <report-tooltip :message="tooltip.partners" />
      </div>
    </div>

    <slot name="period" />

    <div :class="$style.panel">
      <selectabling-panel
        v-model="panel.metrics"
        :label="$t('common.info')"
        @click="handleMetrics"
      />
      <div :class="$style.tooltip">
        <report-tooltip :message="tooltip.metrics" />
      </div>
    </div>
  </substrate-mount>
</template>

<script>
import { EVENT_BUS } from '@/eventBus';

import SubstrateMount from '@/components/capusta/SubstrateMount';
import SelectablingPanel from 'molecules/panels/SelectablingPanel';
import ReportTooltip from './components/ReportTooltip';

import {
  TRANSACTION_TYPES_MAP,
  TRANSACTION_STATUSES_MAP,
  TRANSACTION_METRICS_MAP,
  TRANSACTION_TYPE_LIST,
  TRANSACTION_STATUS_LIST,
  TRANSACTION_METRIC_LIST,
  PARTNER_METRIC_LIST,
  PARTNER_METRICS_MAP
} from './const';

export default {
  name: 'ReportBuilderForm',
  components: {
    SubstrateMount,
    SelectablingPanel,
    ReportTooltip,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    metrics: {
      type: Array,
      required: true,
    },
    partners: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      panel: {
        transactionTypes: this.$t('common.all'),
        transactionStatuses: this.$t('common.successfuls'),
        partners: this.$t('common.all'),
        metrics: this.$t('common.all'),
      },
      tooltip: {
        transactionTypes: '',
        partners: '',
        metrics: '',
      },
    };
  },
  computed: {
    reportType() {
      return this.$route.query.type;
    },
    isPartnerReport() {
      return this.reportType === 'PARTNER';
    },
    isTransactionReport() {
      return this.reportType === 'TRANSACTION';
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(val) {
        this.initializeTransactionTypes(val);
        this.initializeTransactionStatuses(val);
        this.initializeMetrics(val);
        this.initializePartners(val);
      },
    },
    partners: {
      deep: true,
      immediate: true,
      handler() {
        this.initializePartners(this.value);
      },
    },
  },
  methods: {
    localizeValues(values, map) {
      return values.map(value => this.$t(map[value]));
    },
    initializeTransactionTypes(data) {
      const { transactionTypes } = data;
      const localizedAllTypesList = this.localizeValues(TRANSACTION_TYPE_LIST, TRANSACTION_TYPES_MAP);
      const localizedAllUniqsTypesList = [...new Set(localizedAllTypesList)];

      if (this.$validate.isEmptyString(transactionTypes)) {
        this.panel.transactionTypes = this.$t('common.all');
        this.tooltip.transactionTypes = localizedAllUniqsTypesList.join(', ');
        return;
      }

      const localizedTypesList = this.localizeValues(transactionTypes.split(','), TRANSACTION_TYPES_MAP);
      const localizedUniqsTypesList = [...new Set(localizedTypesList)];
      const isAllTransactionTypes = localizedUniqsTypesList.length === localizedAllUniqsTypesList.length;

      this.panel.transactionTypes = isAllTransactionTypes
        ? this.$t('common.all') : localizedUniqsTypesList.join(', ');

      this.tooltip.transactionTypes = localizedUniqsTypesList.join(', ');
    },
    initializeTransactionStatuses(data) {
      const { transactionStatuses } = data;

      if (this.$validate.isEmptyString(transactionStatuses)) {
        this.panel.transactionStatuses = this.$t('common.all');
        return;
      }

      const localizedStatusesList = this.localizeValues(transactionStatuses.split(','), TRANSACTION_STATUSES_MAP);
      const isAllTransactionStatuses = localizedStatusesList.length === TRANSACTION_STATUS_LIST.length;

      this.panel.transactionStatuses = isAllTransactionStatuses
        ? this.$t('common.all') : localizedStatusesList.join(', ');
    },
    initializeMetrics(data) {
      const { metrics } = data;

      if (this.$validate.isEmptyArray(metrics)) {
        const metricsValues = this.isTransactionReport ? TRANSACTION_METRIC_LIST : PARTNER_METRIC_LIST;
        const metricsMap = this.isTransactionReport ? TRANSACTION_METRICS_MAP : PARTNER_METRICS_MAP;
        const localizedMetricList = this.localizeValues(metricsValues, metricsMap);

        this.panel.metrics = this.$t('common.all');
        this.tooltip.metrics = localizedMetricList.join(', ');
        return;
      }

      const metricsDescriptionsList = metrics.map(metric => this.$t(metric.description));
      const allMetricsLength = this.isTransactionReport ? TRANSACTION_METRIC_LIST.length : PARTNER_METRIC_LIST.length;
      const isAllMetrics = metricsDescriptionsList.length === allMetricsLength;

      this.panel.metrics = isAllMetrics ? this.$t('common.all') : metricsDescriptionsList.join(', ');
      this.tooltip.metrics = metricsDescriptionsList.join(', ');
    },
    initializePartners(data) {
      const { partners } = data;

      if (this.$validate.isEmptyArray(partners)) {
        this.panel.partners = this.$t('common.all');
        this.tooltip.partners = this.partners.map(value => value.name).join(', ');
        return;
      }

      const normalizedValue = partners.map(partner => partner.name).join(', ');
      const isAllPartners = partners.length === this.partners.length;

      this.panel.partners = isAllPartners ? this.$t('common.all') : normalizedValue;
      this.tooltip.partners = normalizedValue;
    },
    handleTransactionTypes() {
      EVENT_BUS.$emit('SELECTING_TRANSACTION_TYPES', this.value.transactionTypes);
    },
    handleTransactionStatuses() {
      EVENT_BUS.$emit('SELECTING_TRANSACTION_STATUSES', this.value.transactionStatuses);
    },
    handleMetrics() {
      EVENT_BUS.$emit('SELECTING_METRICS', this.value.metrics);
    },
    handlePartners() {
      EVENT_BUS.$emit('SELECTING_PARTNERS', this.value.partners);
    },
  },
};
</script>

<style lang="sass" module>
  .title
    font-weight: 400
    font-size: 20px
    line-height: 24px
    color: #fff

  .tooltip
    width: 250px
    display: none
    position: absolute
    top: 95%
    left: 20px

  .panel
    position: relative

    &:hover
      .tooltip
        display: block
</style>
